import { FormEvent, useEffect, useState } from "react";
import { Edit, ImagePlus, Plus, RefreshCw, Save, Search, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminTeamMember,
  AdminTeamMemberPayload,
  AdminUser,
  PaginationMeta,
  deleteAdminTeamMember,
  deleteAdminTeamMemberImage,
  getCurrentAdmin,
  listAdminTeamMembers,
  logout,
  saveAdminTeamMember,
  uploadAdminTeamMemberImage,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminSwitchCard } from "./components/AdminChoiceControl";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

type FormState = {
  Name: string;
  Title: string;
  Description: string;
  ImageUrl: string;
  IsActive: boolean;
  SortOrder: string;
};

const emptyForm: FormState = { Name: "", Title: "", Description: "", ImageUrl: "", IsActive: true, SortOrder: "100" };

function itemToForm(item: AdminTeamMember): FormState {
  return {
    Name: item.Name,
    Title: item.Title,
    Description: item.Description,
    ImageUrl: item.ImageUrl || "",
    IsActive: item.IsActive,
    SortOrder: String(item.SortOrder || 100),
  };
}

export default function AdminTeamMembers() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminTeamMember[]>([]);
  const [counts, setCounts] = useState({ Active: 0, Inactive: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [editingItem, setEditingItem] = useState<AdminTeamMember | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminTeamMembers(filters);
      setItems(data.Items);
      setCounts(data.Counts || { Active: 0, Inactive: 0 });
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "團隊成員資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  function openCreateForm() {
    setEditingItem(null);
    setForm(emptyForm);
    setFormMessage("");
    setFormOpen(true);
  }

  function openEditForm(item: AdminTeamMember) {
    setEditingItem(item);
    setForm(itemToForm(item));
    setFormMessage("");
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
  }

  async function handleImageUpload(file: File) {
    setUploadingImage(true);
    setFormMessage("");
    try {
      const previousUrl = form.ImageUrl;
      const url = await uploadAdminTeamMemberImage(file);
      setForm((current) => ({ ...current, ImageUrl: url }));
      if (previousUrl) {
        await deleteAdminTeamMemberImage(previousUrl).catch(() => undefined);
      }
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "圖片上傳失敗，請稍後再試。");
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleRemoveImage() {
    if (form.ImageUrl) {
      await deleteAdminTeamMemberImage(form.ImageUrl).catch(() => undefined);
    }
    setForm((current) => ({ ...current, ImageUrl: "" }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.Name.trim()) { setFormMessage("請輸入姓名。"); return; }
    if (!form.Title.trim()) { setFormMessage("請輸入職稱。"); return; }
    if (!form.Description.trim()) { setFormMessage("請輸入簡介。"); return; }

    const payload: AdminTeamMemberPayload = {
      Name: form.Name.trim(),
      Title: form.Title.trim(),
      Description: form.Description.trim(),
      ImageUrl: form.ImageUrl.trim(),
      IsActive: form.IsActive,
      SortOrder: Number(form.SortOrder || 100),
    };

    setSaving(true);
    setFormMessage("");
    try {
      await saveAdminTeamMember(editingItem?.TeamMemberId || null, payload);
      closeForm();
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "團隊成員儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: AdminTeamMember) {
    const confirmed = await confirm({
      title: "刪除團隊成員？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.Name}</strong> 嗎？
          <br />
          刪除後前台「關於我們」頁面也會立即移除，且無法復原。
        </>
      ),
      confirmText: "刪除成員",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminTeamMember(item.TeamMemberId);
      if (item.ImageUrl) {
        await deleteAdminTeamMemberImage(item.ImageUrl).catch(() => undefined);
      }
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "團隊成員刪除失敗。");
    }
  }

  const allItemCount = counts.Active + counts.Inactive || pagination.Total;

  return (
    <AdminLayout sectionLabel="內容管理" title="專業團隊管理" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="hidden gap-3 md:grid md:flex-1 md:grid-cols-3">
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">全部成員</p>
            <p className="mt-1 text-2xl font-bold text-[#203d32]">{allItemCount}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Active", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">上架中</p>
            <p className="mt-1 text-2xl font-bold text-[#245438]">{counts.Active}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Inactive", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">已下架</p>
            <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Inactive}</p>
          </button>
        </div>
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="team-member-status">狀態</label>
          <select id="team-member-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Active">上架</option>
            <option value="Inactive">下架</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="team-member-keyword">關鍵字</label>
          <input id="team-member-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="姓名或職稱" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateForm} type="button">
          <Plus size={20} />
          新增成員
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[880px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[70px] px-4 py-3">照片</th>
                <th className="w-[320px] px-4 py-3">成員</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[80px] px-4 py-3">排序</th>
                <th className="w-[150px] px-4 py-3">更新時間</th>
                <th className="w-[160px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>沒有符合條件的團隊成員。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.TeamMemberId}>
                  <td className="px-4 py-4">
                    {item.ImageUrl ? (
                      <img alt={item.Name} className="h-12 w-12 rounded-full border border-[#dfe6e1] object-cover" src={item.ImageUrl} />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#e9f4ec] text-sm font-bold text-[#245438]">{item.Name.slice(0, 1)}</div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <strong className="block text-[#203d32]">{item.Name}</strong>
                    <div className="mt-1 text-sm text-[#315746]">{item.Title}</div>
                    <div className="mt-1 line-clamp-2 text-xs text-[#7b877f]">{item.Description}</div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.IsActive ? "上架" : "下架"}</span>
                  </td>
                  <td className="px-4 py-4">{item.SortOrder}</td>
                  <td className="px-4 py-4 text-sm text-[#59665f]">{item.UpdatedAt || "-"}</td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => openEditForm(item)} type="button">
                        <Edit size={16} />
                        編輯
                      </button>
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                        <Trash2 size={16} />
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <form className="relative flex max-h-[90vh] w-full max-w-[640px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">{editingItem ? "編輯團隊成員" : "新增團隊成員"}</h2>
                <p className="mt-1 text-sm text-[#7b877f]">會顯示於前台 /about 頁面的「專業團隊」區塊。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}

              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>姓名 <span className="text-[#c3423f]">*</span></span>
                  <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Name} onChange={(event) => setForm((current) => ({ ...current, Name: event.target.value }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>職稱 <span className="text-[#c3423f]">*</span></span>
                  <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Title} onChange={(event) => setForm((current) => ({ ...current, Title: event.target.value }))} />
                </label>
              </div>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>簡介 <span className="text-[#c3423f]">*</span></span>
                <textarea className="min-h-[110px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Description} onChange={(event) => setForm((current) => ({ ...current, Description: event.target.value }))} />
              </label>
              <div className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>照片</span>
                <div className="flex items-center gap-4">
                  {form.ImageUrl ? (
                    <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-full border border-[#dfe6e1]">
                      <img alt="團隊成員照片" className="h-full w-full object-cover" src={form.ImageUrl} />
                      <button
                        aria-label="移除照片"
                        className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center bg-black/50 text-white"
                        onClick={handleRemoveImage}
                        type="button"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex h-24 w-24 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-full border border-dashed border-[#cfd8d2] text-[#9aa59f] hover:border-[#315746] hover:text-[#315746]">
                      <ImagePlus size={22} />
                      <span className="text-[11px] font-bold">上傳照片</span>
                      <input
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        type="file"
                        onChange={(event) => event.target.files?.[0] && handleImageUpload(event.target.files[0])}
                      />
                    </label>
                  )}
                  <div className="text-xs font-normal text-[#7b877f]">
                    {uploadingImage ? "上傳中..." : "支援 JPG、PNG、WebP、GIF，檔案大小 5MB 以內。"}
                  </div>
                </div>
              </div>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                排序（數字越小越前面）
                <input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} />
              </label>
              <AdminSwitchCard checked={form.IsActive} description="下架後不會顯示於前台「關於我們」頁面。" label="上架顯示" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                取消
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存成員"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
